import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import { API_BASE_URL } from "../../../utils/api";
import api from "../../../utils/api";

const CommentsTab = ({ comments, onAddCommentClick }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState({});
  const [loadingAttachments, setLoadingAttachments] = useState({});
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // 🧠 Fetch files for a specific comment
  const fetchAttachments = async (commentId) => {
    if (attachments[commentId] || loadingAttachments[commentId]) return;

    console.log("📦 Fetching attachments for comment_id:", commentId);
    setLoadingAttachments((prev) => ({ ...prev, [commentId]: true }));

    try {
      const { data } = await api.get(`/workorder-comment-files/${commentId}`);
      console.log("✅ Response for comment_id", commentId, ":", data);
      setAttachments((prev) => ({ ...prev, [commentId]: data || [] }));
    } catch (err) {
      console.error(`❌ Failed to fetch attachments for comment ${commentId}:`, err);
    } finally {
      setLoadingAttachments((prev) => ({ ...prev, [commentId]: false }));
    }
  };
  useEffect(() => {
  comments?.forEach((comment) => {
    fetchAttachments(comment.comment_id);
  });
}, [comments]);

 const handleAddComment = () => {
  if (onAddCommentClick) {
    onAddCommentClick(); // Just open the modal
  }
};



const handleDownload = (file) => {
  if (!file?.file_url) return;

  // Extract filename correctly
  const filename = file.file_url.split(/(\\|\/)/g).pop();

  const url = `${API_BASE_URL}/uploads/${encodeURIComponent(filename)}`;
  console.log("⬇️ Downloading from:", url);

  window.location.href = url;
};




  const handleImageClick = (url) => {
    setSelectedImage(url);
    setOpenImageModal(true);
  };

  // 🧩 Helper: build URL exactly like in fetchAttachedFiles
  const buildPreviewUrl = (file) => {
    const fileName = file.file_url?.split(/(\\|\/)/g).pop();
    if (!fileName) return "";

    if (file.file_type && file.file_type.startsWith("image/")) {
      // 🖼️ Same logic as fetchAttachedFiles — hit /workordercommentimages
      return `${API_BASE_URL}/workordercommentimages/${encodeURIComponent(fileName)}`;
    }
    // 📄 Non-images use direct download path
    return `${API_BASE_URL}${file.file_url}`;
  };

  const renderFilePreview = (file) => {
    const fileUrl = buildPreviewUrl(file);
    const fileName = file.file_name || "";
    const fileType = file.file_type || "";

    console.log("🧾 Rendering file:", { fileName, fileType, fileUrl });

    const isImage =
      fileType.startsWith("image/") ||
      /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
    const isPDF = fileType === "application/pdf" || fileName.endsWith(".pdf");

    if (isImage) {
      return (
        <Card
          elevation={3}
          sx={{
            p: 1,
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            maxWidth: 120,
            cursor: "pointer",
            transition: "transform 0.2s ease-in-out",
            "&:hover": { transform: "scale(1.05)" },
          }}
          onClick={() => handleImageClick(fileUrl)}
        >
          <img
            src={fileUrl}
            alt={fileName}
            style={{
              width: "100%",
              height: "auto",
              borderRadius: "8px",
              border: "1px solid #ccc",
              objectFit: "cover",
            }}
            onError={(e) => {
              if (!e.target.dataset.fallback) {
                e.target.dataset.fallback = "true";
                e.target.src = "https://placehold.co/120x120?text=No+Preview";
              }
            }}
          />
        </Card>
      );
    }

    // 📄 Fallback for non-image files
    return (
      <Card
        elevation={3}
        sx={{
          p: 2,
          width: 120,
          height: 120,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "12px",
          backgroundColor: "#fafafa",
          border: "1px solid #ccc",
          cursor: "pointer",
          transition: "transform 0.2s ease-in-out",
          "&:hover": { transform: "scale(1.05)" },
        }}
         onClick={() => handleDownload(file)}
      >
        {isPDF ? (
          <PictureAsPdfIcon sx={{ fontSize: 40, color: "error.main" }} />
        ) : (
          <DescriptionIcon sx={{ fontSize: 40, color: "primary.main" }} />
        )}
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            textAlign: "center",
            wordBreak: "break-word",
            color: "text.secondary",
          }}
        >
          {fileName.length > 15 ? fileName.slice(0, 15) + "..." : fileName}
        </Typography>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Comments
      </Typography>

      {Array.isArray(comments) && comments.length > 0 ? (
        comments.map((comment) => {
          const isInternal =
            comment.is_internal === true || comment.is_internal === 1;
          const commentFiles = attachments[comment.comment_id] || [];

          return (
            <Card
              key={comment.comment_id}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: "12px",
                backgroundColor: isInternal ? "#fbe9e7" : "#e3f2fd",
                boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
              }}
              
            >
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: isInternal ? "red" : "blue",
                }}
              >
                {isInternal ? "Internal Comment" : "External Comment"}
              </Typography>

              <Typography variant="body2" sx={{ mb: commentFiles.length ? 1 : 0 }}>
                {comment.comment_text}
              </Typography>

              {/* Loading Spinner */}
              {loadingAttachments[comment.comment_id] && (
                <Box sx={{ textAlign: "center", mt: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}

              {/* Attachments Section */}
              {commentFiles.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mt: 1,
                    justifyContent: "flex-start",
                  }}
                >
                  {commentFiles.map((file) => (
                    <Box key={file.id}>{renderFilePreview(file)}</Box>
                  ))}
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  fontStyle: "italic",
                  display: "block",
                  mt: 1,
                }}
              >
                🕒 Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                {new Date(comment.sent_at).toLocaleString()}
              </Typography>
            </Card>
          );
        })
      ) : (
        <Typography variant="body2" color="textSecondary">
          No comments yet. Be the first to add one!
        </Typography>
      )}

      {/* ➕ Add Comment Button */}
      <Button
  variant="contained"
  color="primary"
  onClick={handleAddComment}
  sx={{ mt: 2 }}
>
  Add Comment
</Button>


      {/* ✅ Snackbar */}
      <Snackbar
        open={showConfirm}
        autoHideDuration={3000}
        onClose={() => setShowConfirm(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Comment added successfully!
        </Alert>
      </Snackbar>

      {/* 🖼️ Image Modal */}
      <Dialog open={openImageModal} onClose={() => setOpenImageModal(false)} maxWidth="md">
        <DialogContent sx={{ position: "relative", textAlign: "center" }}>
          <IconButton
            onClick={() => setOpenImageModal(false)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={selectedImage}
            alt="Enlarged Attachment"
            style={{
              width: "100%",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
            onError={(e) => {
              if (!e.target.dataset.fallback) {
                e.target.dataset.fallback = "true";
                e.target.src = "https://placehold.co/800x600?text=No+Preview";
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CommentsTab;
